import { Controller } from "@hotwired/stimulus"

const ICON_CONFIG = {
  "check-circle":     { bg: "bg-green-400/15",  color: "text-green-400",  path: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" },
  "x-circle":         { bg: "bg-red-400/15",    color: "text-red-400",    path: "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" },
  "clock":            { bg: "bg-amber-400/15",  color: "text-amber-400",  path: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" },
  "arrow-right-left": { bg: "bg-blue-400/15",   color: "text-blue-400",   path: "M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" },
  "banknotes":        { bg: "bg-green-400/15",  color: "text-green-400",  path: "M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" },
  "sparkles":         { bg: "bg-purple-400/15", color: "text-purple-400", path: "M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" },
  "bell":             { bg: "bg-slate-400/15",  color: "text-slate-400",  path: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" }
}

export default class extends Controller {
  static targets = ["backdrop", "dialog", "title", "body", "meta", "icon", "actionButton"]

  connect() {
    this.keydownHandler = this._handleKeydown.bind(this)
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydownHandler)
  }

  open(event) {
    const trigger = event.currentTarget
    const id = trigger.dataset.notificationId
    const title = trigger.dataset.notificationTitle || ""
    const body = trigger.dataset.notificationBody || ""
    const actionUrl = trigger.dataset.notificationActionUrl || ""
    const createdAt = trigger.dataset.notificationCreatedAt || ""
    const iconKey = trigger.dataset.notificationIcon || "bell"
    const iconConfig = ICON_CONFIG[iconKey] || ICON_CONFIG["bell"]

    if (this.hasTitleTarget) this.titleTarget.textContent = title
    if (this.hasBodyTarget) this.bodyTarget.textContent = body
    if (this.hasMetaTarget) this.metaTarget.textContent = createdAt

    if (this.hasIconTarget) {
      this.iconTarget.className = `shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${iconConfig.bg}`
      this.iconTarget.innerHTML = `<svg class="w-6 h-6 ${iconConfig.color}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="${iconConfig.path}" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/></svg>`
    }

    if (this.hasActionButtonTarget) {
      if (actionUrl && actionUrl.length > 0) {
        this.actionButtonTarget.href = actionUrl
        this.actionButtonTarget.classList.remove("hidden")
      } else {
        this.actionButtonTarget.classList.add("hidden")
      }
    }

    this._prepareInitialState()
    this._show()

    if (id) {
      this._markAsRead(id, trigger)
    }
  }

  close() {
    this._hide()
  }

  closeBackdrop(event) {
    if (event.target === this.backdropTarget) {
      this._hide()
    }
  }

  // ── Private ──

  _prepareInitialState() {
    if (this._isMobile()) {
      this.dialogTarget.classList.remove("scale-95", "scale-100", "opacity-100")
      this.dialogTarget.classList.add("translate-y-full", "opacity-0")
    } else {
      this.dialogTarget.classList.remove("translate-y-full", "translate-y-0")
      this.dialogTarget.classList.add("scale-95", "opacity-0")
    }
  }

  _isMobile() {
    return window.innerWidth < 640
  }

  _show() {
    this.backdropTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.keydownHandler)
    // Double rAF: first frame registers the element as visible,
    // second frame triggers the transition from initial state.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.backdropTarget.classList.add("opacity-100")
        if (this._isMobile()) {
          this.dialogTarget.classList.remove("translate-y-full", "opacity-0")
          this.dialogTarget.classList.add("translate-y-0", "opacity-100")
        } else {
          this.dialogTarget.classList.remove("scale-95", "opacity-0")
          this.dialogTarget.classList.add("scale-100", "opacity-100")
        }
      })
    })
  }

  _hide() {
    this.backdropTarget.classList.remove("opacity-100")
    if (this._isMobile()) {
      this.dialogTarget.classList.remove("translate-y-0", "opacity-100")
      this.dialogTarget.classList.add("translate-y-full", "opacity-0")
    } else {
      this.dialogTarget.classList.remove("scale-100", "opacity-100")
      this.dialogTarget.classList.add("scale-95", "opacity-0")
    }
    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this.keydownHandler)
    // Wait for CSS transition to finish before hiding the element.
    this.dialogTarget.addEventListener("transitionend", () => {
      this.backdropTarget.classList.add("hidden")
    }, { once: true })
  }

  _handleKeydown(event) {
    if (event.key === "Escape") this._hide()
  }

  _markAsRead(id, trigger) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    fetch(`/notificaciones/${id}/leida`, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      }
    })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(data => {
        this._updateBellBadge(data.unread_count)
        this._markTriggerAsRead(trigger)
      })
      .catch(e => console.error("[notification-modal] mark_read failed", e))
  }

  _updateBellBadge(count) {
    const bell = document.querySelector("[data-controller~='notification-bell']")
    if (!bell) return
    const badge = bell.querySelector("[data-notification-bell-target='badge']")
    const countEl = bell.querySelector("[data-notification-bell-target='count']")
    if (countEl) countEl.textContent = count
    if (badge) badge.classList.toggle("hidden", count === 0)
  }

  _markTriggerAsRead(trigger) {
    trigger.classList.remove("bg-slate-800/50", "border-l-2", "border-accent")
    const title = trigger.querySelector("p.text-sm")
    if (title) {
      title.classList.remove("font-semibold", "text-white")
      title.classList.add("font-normal", "text-slate-400")
    }
    const dot = trigger.querySelector("span.bg-accent.rounded-full")
    if (dot) dot.remove()
  }
}
